/**
 * Наряд-допуск (КЧ-05): анализ рисков JSA 5 × 5 и отпечаток содержания SHA-256. Нужен ли анализ для вида работ и
 * границы уровней риска — из настроек нарядов компании; все проверки повторяет сервер.
 */
import {
  type PermitDetail,
  type PermitRiskInput,
  type PermitRiskView,
  RISK_LEVELS,
  RISK_LIKELIHOOD,
  RISK_SEVERITY,
  type RiskLevel,
  riskLevel,
} from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const LEVEL_TONE: Record<RiskLevel, Tone> = {
  low: 'success',
  medium: 'info',
  high: 'warning',
  unacceptable: 'danger',
};

const failed = (e: unknown) => toast(e instanceof Error ? e.message : 'Не получилось', 'danger');

function RiskBadge({ score, level }: { score: number; level: RiskLevel }) {
  return (
    <Badge tone={LEVEL_TONE[level]}>
      {score} · {RISK_LEVELS[level].toLowerCase()}
    </Badge>
  );
}

type Draft = Omit<PermitRiskInput, 'severity' | 'likelihood' | 'residualSeverity' | 'residualLikelihood'> & {
  severity: string;
  likelihood: string;
  residualSeverity: string;
  residualLikelihood: string;
};
const EMPTY: Draft = {
  step: '',
  hazard: '',
  severity: '3',
  likelihood: '3',
  controls: '',
  residualSeverity: '3',
  residualLikelihood: '2',
};
const draftOf = (r: PermitRiskView): Draft => ({
  step: r.step,
  hazard: r.hazard,
  severity: String(r.severity),
  likelihood: String(r.likelihood),
  controls: r.controls,
  residualSeverity: String(r.residualSeverity),
  residualLikelihood: String(r.residualLikelihood),
});

function ScaleSelect({
  id,
  label,
  value,
  labels,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  labels: Record<number, string>;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Select id={id} className="h-11 md:h-10" value={value} onChange={(e) => onChange(e.target.value)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={String(n)}>
            {n} — {labels[n]}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function RiskForm({
  d,
  editing,
  onDone,
  onCancel,
}: {
  d: PermitDetail;
  editing: PermitRiskView | null;
  onDone: (d: PermitDetail) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState<Draft>(editing ? draftOf(editing) : EMPTY);
  const set = (patch: Partial<Draft>) => setF((x) => ({ ...x, ...patch }));
  const score = Number(f.severity) * Number(f.likelihood);
  const residual = Number(f.residualSeverity) * Number(f.residualLikelihood);
  const save = useMutation({
    mutationFn: () => {
      const body: PermitRiskInput = {
        step: f.step.trim(),
        hazard: f.hazard.trim(),
        severity: Number(f.severity),
        likelihood: Number(f.likelihood),
        controls: f.controls.trim(),
        residualSeverity: Number(f.residualSeverity),
        residualLikelihood: Number(f.residualLikelihood),
      };
      return editing
        ? api<PermitDetail>(`/quality/permits/${d.id}/risks/${editing.id}`, { method: 'PUT', body })
        : api<PermitDetail>(`/quality/permits/${d.id}/risks`, { body });
    },
    onSuccess: (r) => {
      onDone(r);
      setF(EMPTY);
      toast(editing ? 'Строка анализа рисков сохранена' : 'Риск добавлен');
      onCancel();
    },
  });
  const p = editing ? `risk-${editing.id.slice(0, 8)}` : 'risk-new';
  return (
    <form
      className="flex flex-col gap-3 border-t border-line pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Шаг работы" hint="например: вскрытие люка резервуара" htmlFor={`${p}-step`}>
          <Input
            id={`${p}-step`}
            className="h-11 md:h-10"
            maxLength={250}
            value={f.step}
            onChange={(e) => set({ step: e.target.value })}
          />
        </Field>
        <Field label="Опасность" hint="например: выброс сероводорода" htmlFor={`${p}-hazard`}>
          <Input
            id={`${p}-hazard`}
            className="h-11 md:h-10"
            maxLength={250}
            value={f.hazard}
            onChange={(e) => set({ hazard: e.target.value })}
          />
        </Field>
        <ScaleSelect
          id={`${p}-sev`}
          label="Тяжесть"
          value={f.severity}
          labels={RISK_SEVERITY}
          onChange={(v) => set({ severity: v })}
        />
        <ScaleSelect
          id={`${p}-lik`}
          label="Вероятность"
          value={f.likelihood}
          labels={RISK_LIKELIHOOD}
          onChange={(v) => set({ likelihood: v })}
        />
      </div>
      <p className="text-[14px] text-ink">
        Риск до мер: <RiskBadge score={score} level={riskLevel(score, d.riskLimits)} />
      </p>
      <Field label="Меры управления риском" htmlFor={`${p}-controls`}>
        <Textarea
          id={`${p}-controls`}
          rows={3}
          maxLength={2000}
          value={f.controls}
          onChange={(e) => set({ controls: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <ScaleSelect
          id={`${p}-rsev`}
          label="Тяжесть после мер"
          value={f.residualSeverity}
          labels={RISK_SEVERITY}
          onChange={(v) => set({ residualSeverity: v })}
        />
        <ScaleSelect
          id={`${p}-rlik`}
          label="Вероятность после мер"
          value={f.residualLikelihood}
          labels={RISK_LIKELIHOOD}
          onChange={(v) => set({ residualLikelihood: v })}
        />
      </div>
      <p className="text-[14px] text-ink">
        Остаточный риск: <RiskBadge score={residual} level={riskLevel(residual, d.riskLimits)} />
        {residual > score ? <span className="ml-2 text-danger">— не может быть выше исходного</span> : null}
      </p>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="secondary"
          className="h-11 md:h-10"
          loading={save.isPending}
          disabled={!f.step.trim() || !f.hazard.trim() || !f.controls.trim() || residual > score}
        >
          {editing ? 'Сохранить строку' : 'Добавить риск'}
        </Button>
        {editing ? (
          <Button variant="ghost" className="h-11 md:h-10" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  );
}

export function PermitRisksPanel({ d, onDone }: { d: PermitDetail; onDone: (d: PermitDetail) => void }) {
  const [editId, setEditId] = useState<string | null>(null);
  if (!d.jsaRequired && !d.risks.length && !d.can.risks) return null;
  const draftBlockers =
    d.status === 'draft' ? d.blockers.filter((b) => b.startsWith('Анализ рисков') || b.includes('JSA')) : [];
  const L = d.riskLimits;
  return (
    <Panel
      title={`Анализ рисков JSA (${d.risks.length})`}
      meta={`тяжесть × вероятность, 1–25: средний от ${L.medium}, высокий от ${L.high}, недопустимый от ${L.unacceptable}${d.jsaRequired ? '; для этого вида работ обязателен' : ''}`}
      className="mb-6"
    >
      {draftBlockers.length ? (
        <ul className="mb-4 list-disc pl-5 text-[14px] text-danger">
          {draftBlockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : null}
      {d.risks.length ? (
        <ol className="mb-4 flex flex-col gap-2">
          {d.risks.map((r, i) =>
            editId === r.id ? (
              <li key={r.id}>
                <RiskForm d={d} editing={r} onDone={onDone} onCancel={() => setEditId(null)} />
              </li>
            ) : (
              <li
                key={r.id}
                className="flex flex-col gap-2 rounded-md border border-line p-3 text-[14px] text-ink"
              >
                <span className="font-semibold [overflow-wrap:anywhere]">
                  {i + 1}. {r.step}
                </span>
                <span className="[overflow-wrap:anywhere]">Опасность: {r.hazard}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-ink-2">
                    до мер {r.severity} × {r.likelihood}
                  </span>
                  <RiskBadge score={r.score} level={r.level} />
                  <span className="text-ink-2">
                    после мер {r.residualSeverity} × {r.residualLikelihood}
                  </span>
                  <RiskBadge score={r.residual} level={r.residualLevel} />
                </span>
                <span className="text-ink-2 [overflow-wrap:anywhere] whitespace-pre-line">
                  Меры: {r.controls}
                </span>
                {d.can.risks ? (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="ghost" className="h-11 md:h-9" onClick={() => setEditId(r.id)}>
                      Изменить
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-11 text-danger md:h-9"
                      onClick={() =>
                        api<PermitDetail>(`/quality/permits/${d.id}/risks/${r.id}`, { method: 'DELETE' })
                          .then(onDone)
                          .catch(failed)
                      }
                    >
                      Убрать
                    </Button>
                  </div>
                ) : null}
              </li>
            ),
          )}
        </ol>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">
          Анализа рисков нет.
          {d.can.risks ? ' Разберите работу по шагам: опасность, оценка, меры и риск после мер.' : ''}
        </p>
      )}
      {d.can.risks && !editId ? (
        <RiskForm d={d} editing={null} onDone={onDone} onCancel={() => setEditId(null)} />
      ) : null}
    </Panel>
  );
}

/** Отпечаток содержания: под ним ставятся подписи */
export function PermitHashNote({ d }: { d: PermitDetail }) {
  if (!d.contentHash) return null;
  return (
    <div className="mt-4 flex flex-col gap-1 border-t border-line pt-3 text-[13px] text-ink-2">
      <span>
        Отпечаток содержания SHA-256:{' '}
        <code className="break-all font-mono text-[12px] text-ink">{d.contentHash}</code>
      </span>
      {d.hashOk === false ? (
        <span className="font-semibold text-danger" role="alert">
          Содержание наряда не совпадает с отпечатком, под которым его подписывали.
        </span>
      ) : (
        <span>
          {d.events.some((e) => e.kind === 'extended')
            ? 'Содержание не менялось после продления (отпечаток пересчитан при продлении, подписи — под прежним).'
            : 'Содержание не менялось после отправки на подпись.'}
        </span>
      )}
    </div>
  );
}
